import React from 'react';
import { Link, Typography } from '@mui/material';

// Regular expressions for detecting different types of links
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_REGEX = /^\+?[\d\s-()]{10,}$/; // Basic phone number regex
const LINKEDIN_REGEX = /^(https?:\/\/)?([\w]+\.)?linkedin\.com\/[\w-]+\/?.*$/;
const FACEBOOK_REGEX = /^(https?:\/\/)?([\w]+\.)?facebook\.com\/[a-zA-Z0-9(\.\?)?]+$/;
const TWITTER_REGEX = /^(https?:\/\/)?([\w]+\.)?twitter\.com\/[a-zA-Z0-9_]+$/;
const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=@#]*)?$/;

export interface LinkifyOptions {
  color?: string;
  underline?: 'none' | 'hover' | 'always';
  fontSize?: string | number;
}

interface LinkProps {
  href: string;
  children: React.ReactNode;
  target?: string;
  rel?: string;
  sx?: {
    color?: string;
    textDecoration?: string;
    fontSize?: string | number;
    cursor?: string;
    '&:hover'?: {
      textDecoration?: string;
    };
  };
}

export const isEmail = (text: string): boolean => EMAIL_REGEX.test(text);
export const isPhone = (text: string): boolean => PHONE_REGEX.test(text);
export const isLinkedIn = (text: string): boolean => LINKEDIN_REGEX.test(text);
export const isFacebook = (text: string): boolean => FACEBOOK_REGEX.test(text);
export const isTwitter = (text: string): boolean => TWITTER_REGEX.test(text);
export const isUrl = (text: string): boolean => URL_REGEX.test(text);

const createLinkProps = (text: string, options: LinkifyOptions, type: 'email' | 'phone' | 'url'): LinkProps => {
  const { color = 'primary', underline = 'hover', fontSize } = options;

  const commonProps: LinkProps = {
    href: '',
    children: text,
    sx: {
      color,
      textDecoration: underline === 'always' ? 'underline' : 'none',
      fontSize,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: underline === 'none' ? 'none' : 'underline'
      }
    }
  };

  switch (type) {
    case 'email':
      return { ...commonProps, href: `mailto:${text}` };
    case 'phone':
      const cleanPhone = text.replace(/[^\d+]/g, '');
      return { ...commonProps, href: `tel:${cleanPhone}` };
    case 'url':
      const url = text.startsWith('http') ? text : `https://${text}`;
      return {
        ...commonProps,
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer'
      };
    default:
      return commonProps;
  }
};

export const linkifyText = (text: string | null | undefined, options: LinkifyOptions = {}): React.ReactNode => {
  if (!text) return text;

  // Check if text contains multiple URLs/emails/phones separated by commas
  if (text.includes(',')) {
    const parts = text.split(',').map(part => part.trim()).filter(part => part.length > 0);
    
    // If we have multiple parts, process each one individually
    if (parts.length > 1) {
      return parts.map((part, index) => {
        const linkifiedPart = linkifyText(part, options);
        
        // If the part was linkified, return it as a React element
        if (React.isValidElement(linkifiedPart)) {
          return React.createElement(React.Fragment, { key: index }, [
            linkifiedPart,
            index < parts.length - 1 ? ', ' : null
          ]);
        }
        
        // If not linkified, return as text with comma separator
        return React.createElement(React.Fragment, { key: index }, [
          part,
          index < parts.length - 1 ? ', ' : null
        ]);
      });
    }
  }

  // Handle single items
  if (isEmail(text)) {
    const props = createLinkProps(text, options, 'email');
    return React.createElement(Link, props);
  }

  if (isPhone(text)) {
    const props = createLinkProps(text, options, 'phone');
    return React.createElement(Link, props);
  }

  if (isLinkedIn(text) || isFacebook(text) || isTwitter(text) || isUrl(text)) {
    const props = createLinkProps(text, options, 'url');
    return React.createElement(Link, props);
  }

  return text;
};

export const LinkifiedTableCell: React.FC<{
  value: string | null | undefined;
  options?: LinkifyOptions;
}> = ({ value, options }) => {
  return React.createElement(Typography, {
    variant: 'body2',
    children: linkifyText(value, options)
  });
};